import React, { useEffect, useState, useCallback } from 'react'
import { Row, Col } from 'react-bootstrap'
import DataGridComponent from '@components/common/DataGridComponent/DataGridComponent'
import { Claim, Procedure } from '@dataTypes/claimFeature.types'
import { getClaimDetailsColumns } from './sharedClaimDetails'
import './AddClaimsStyle.css'
import { getProcedureColumns } from './sharedClaimProcedure'
import ConfirmDeleteModal from '../ConfirmDeleteModal/ConfirmDeleteModal.component'
import ClaimSource from './ClaimSource'

type ClaimProcedureTabProps = {
  selectedClaim: Claim
  appointment: {
    data: any[]
    selected: any
    handleSelect: (appointment: any) => void
  }
  treatmentPlan: {
    data: any[]
    selected: any
    handleSelect: (treatmentPlan: any) => void
  }
  handleClaimSourceChange: (claimSource: 0 | 1) => void
  onChangeClaim: (updatedClaim: Claim) => void
  onEditProcedure: (procedure: Procedure, editMode: boolean) => void
}

const ClaimProcedureTab: React.FC<ClaimProcedureTabProps> = ({
  selectedClaim,
  appointment,
  treatmentPlan,
  handleClaimSourceChange,
  onChangeClaim,
  onEditProcedure,
}) => {
  const [claimProcedures, setClaimProcedures] = useState<Procedure[]>(
    selectedClaim.procedures,
  )
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false)
  const [procedureToDelete, setProcedureToDelete] = useState<Procedure | null>(
    null,
  )

  useEffect(() => {
    setClaimProcedures([...selectedClaim.procedures])
  }, [selectedClaim.procedures])

  const handleConfirmDelete = (procedureId: string): void => {
    const procedure = claimProcedures.find((p) => p.procedureId === procedureId)
    if (!procedure) return

    setProcedureToDelete(procedure)
    setShowDeleteModal(true)
  }

  const handleRemoveProcedure = (): void => {
    if (!procedureToDelete) return

    const updatedProcedures = claimProcedures.filter(
      (p) => p.procedureId !== procedureToDelete.procedureId,
    )

    const updatedClaim = { ...selectedClaim, procedures: updatedProcedures }

    setClaimProcedures(updatedProcedures)
    onChangeClaim(updatedClaim)

    setShowDeleteModal(false)
    setProcedureToDelete(null)
  }

  const handleUpdateClinicFee = useCallback(
    (
      procedureId: string,
      newFee: string,
      selectedFeeKey: string,
      phaseName?: string,
    ) => {
      const updatedProcedures = claimProcedures.map((procedure) =>
        procedure.procedureId === procedureId &&
        (selectedClaim.claimSource === 1
          ? procedure.phaseName === phaseName
          : true)
          ? {
              ...procedure,
              amount: newFee,
              selectedClinicFee: newFee,
              selectedClinicFeeSettings: selectedFeeKey,
            }
          : procedure,
      )
      setClaimProcedures(updatedProcedures)
      const updatedClaim = { ...selectedClaim, procedures: updatedProcedures }
      onChangeClaim(updatedClaim)
    },
    [claimProcedures, selectedClaim, onChangeClaim],
  )

  useEffect(() => {
    if (claimProcedures.length === 0) {
      setClaimProcedures([])
    }
  }, [selectedClaim.claimSource])

  return (
    <div>
      <Row className="gx-0">
        <Col className="px-0">
          <div className="details--tab">
            <div className="claim--photo">
              <img
                src={selectedClaim.photo || 'https://dummyimage.com/135x135/'}
                alt={selectedClaim.patientName || 'No Image Available'}
                className="claim-photo-img"
              />
            </div>
            <div className="claim--details-table-container">
              <DataGridComponent
                data={[{ ...selectedClaim }]}
                columns={getClaimDetailsColumns()}
                showCheckbox={false}
                customCSS="claim-details-tab-table"
              />
            </div>
          </div>
        </Col>
      </Row>

      <ClaimSource
        selectedClaim={selectedClaim}
        appointment={appointment}
        treatmentPlan={treatmentPlan}
        handleClaimSourceChange={handleClaimSourceChange}
      />

      {/* Procedures Table */}
      <Row className="mt-2">
        <Col>
          <DataGridComponent
            key={`${claimProcedures.length}-${selectedClaim.claimSource}`}
            data={claimProcedures}
            columns={getProcedureColumns(
              handleConfirmDelete,
              onEditProcedure,
              handleUpdateClinicFee,
              selectedClaim.attachmentRequirements,
              selectedClaim.claimSource,
            )}
            rowHeight={36}
            {...(selectedClaim.claimSource === 1
              ? {
                  groupSettings: {
                    showGroupedColumn: false,
                    columns: ['phaseName'],
                    captionTemplate: '<span>${key}<span>',
                  },
                }
              : {})}
            allowPaging={true}
            pageSize={10}
            showCheckbox={false}
            customCSS="claim-procedure-table"
          />
        </Col>
      </Row>

      <ConfirmDeleteModal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        onConfirm={handleRemoveProcedure}
        entityName={procedureToDelete?.procedureName || 'this procedure'}
      />
    </div>
  )
}

export default ClaimProcedureTab
